import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { IAttendanceFilter, EStatisticsInterval, EStatisticsGraphView, EStatisticsType, EStatisticsDateType } from '../../../../../reducers/data';
import getAttendanceStats from '../../../../../reducers/api/getAttendanceStats';
import Loader from '../../Loader';
import { parseGraphDataAttendance, parseGraphDataOverlay, numberWithSpaces, msToTime } from '../../../../helpers/index';
import { useLingui } from '@lingui/react';
import ColumnGraph from './ColumnGraph';
import { IGraphContentData, IGraphData } from '../../../../interfaces/types';
import PieGraph from './PieGraph';
import XYGraph from './XYGraph';
import TableGraph from './TableGraph';
import { t, Trans } from '@lingui/macro';
import moment from 'moment';

interface IProps {
    filter: IAttendanceFilter
}

// ─── KPI helpers ─────────────────────────────────────────────────────────────

const WEEKDAY_SK_FULL = ['Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota', 'Nedeľa']

/** ISO weekday (1=Mon…7=Sun) → 0-based Mon-first index */
function isoWdToIdx(isoWd: number): number { return isoWd - 1 }

/**
 * Resolve the effective data resolution from the filter.
 *
 * For column / pie: the backend groups by dateType (day/week/month/year) —
 *   there is no interval field, one data point = one dateType bucket.
 * For xy / table:  each point has the granularity of filter.interval.
 *
 * Returns a canonical string used for label formatting and bestDay eligibility.
 */
function effectiveResolution(filter: IAttendanceFilter): EStatisticsInterval | EStatisticsDateType {
    if (filter.type === EStatisticsType.column || filter.type === EStatisticsType.cake) {
        // Column/pie use dateType as the bucket size
        switch (filter.dateType) {
            case EStatisticsDateType.day:   return EStatisticsInterval.day
            case EStatisticsDateType.week:  return EStatisticsInterval.week
            case EStatisticsDateType.month: return EStatisticsInterval.month
            default:                        return EStatisticsInterval.day
        }
    }
    return filter.interval ?? EStatisticsInterval.day
}

/**
 * Format the peak-slot datetime label to match the actual data resolution.
 *   15min / 30min / hour  →  "15.02. 14:00"
 *   day                   →  "15. 02. 2026"
 *   week                  →  "Týždeň 7 / 2026"
 *   month                 →  "Február 2026"
 */
function peakSlotLabel(start: string, res: EStatisticsInterval | EStatisticsDateType): string {
    const m = moment(start)
    switch (res) {
        case EStatisticsInterval.day:
        case EStatisticsDateType.day:    return m.format('DD. MM. YYYY')
        case EStatisticsInterval.week:
        case EStatisticsDateType.week:   return `Týždeň ${m.isoWeek()} / ${m.isoWeekYear()}`
        case EStatisticsInterval.month:
        case EStatisticsDateType.month:  return m.format('MMMM YYYY')
        default:                         return m.format('DD.MM. HH:mm')
    }
}

/**
 * True when each data point represents a multi-day bucket (week / month).
 * In that case weekday analysis is meaningless because one point = many days.
 */
function isMultiDayResolution(res: EStatisticsInterval | EStatisticsDateType): boolean {
    return res === EStatisticsInterval.week  || res === EStatisticsDateType.week  ||
           res === EStatisticsInterval.month || res === EStatisticsDateType.month
}

interface IKpi {
    /** Grand total. null when multiple zones/dates are shown (would double-count). */
    total: number | null
    /** Peak data-point in the dominant (highest-total) series. */
    peak: { label: string; count: number; resLabel: string } | null
    /** Best weekday index (0=Mon…6=Sun). null when resolution is week/month or <2 distinct weekdays. */
    bestDayIdx: number | null
    bestDayAvg: number | null
}

/**
 * Derive KPI values from raw API results, respecting the active filter.
 *
 * Key rules
 * ─────────
 * • total     — only for single series (multi graphView + 1 series, or multi mode)
 * • peak      — always shown when there is data; label format matches data resolution
 * • bestDay   — only shown when resolution is day/hour AND ≥2 distinct weekdays exist
 */
function computeKpi(
    rawResults: IGraphData[] | undefined,
    filter: IAttendanceFilter
): IKpi {
    const empty: IKpi = { total: null, peak: null, bestDayIdx: null, bestDayAvg: null }
    if (!rawResults || rawResults.length === 0) return empty

    const res = effectiveResolution(filter)

    const isMultiSeries = (filter.graphView === EStatisticsGraphView.zone ||
                           filter.graphView === EStatisticsGraphView.date) &&
                          rawResults.length > 1

    // ── Total ────────────────────────────────────────────────────────────────
    let total: number | null = null
    if (!isMultiSeries) {
        total = 0
        rawResults.forEach(s => s.data.forEach(d => { total = (total ?? 0) + Number(d.count) }))
    }

    // ── Peak data point ──────────────────────────────────────────────────────
    // For multi-series pick the dominant (highest total) series first so we
    // compare within one consistent entity rather than across zones/dates.
    const seriesWithTotals = rawResults.map(s => ({
        series: s,
        seriesTotal: s.data.reduce((acc, d) => acc + Number(d.count), 0)
    }))
    const dominant = seriesWithTotals.slice().sort((a, b) => b.seriesTotal - a.seriesTotal)[0]

    let peakCount = 0
    let peakLabel = ''
    // Peak label sub-description: what does one point represent
    const resLabel = (() => {
        switch (res) {
            case EStatisticsInterval.week:
            case EStatisticsDateType.week:   return 'týždeň'
            case EStatisticsInterval.month:
            case EStatisticsDateType.month:  return 'mesiac'
            case EStatisticsInterval.day:
            case EStatisticsDateType.day:    return 'deň'
            default:                         return 'slot'
        }
    })()

    if (dominant) {
        dominant.series.data.forEach(d => {
            const count = Number(d.count)
            if (count > peakCount) {
                peakCount = count
                peakLabel = peakSlotLabel(d.start, res)
            }
        })
    }

    // ── Best weekday ─────────────────────────────────────────────────────────
    // Only meaningful when resolution ≤ day (hour/15min/30min/day).
    // For week/month resolution every point already spans multiple weekdays
    // so weekday attribution is arbitrary — we hide this card.
    let bestDayIdx: number | null = null
    let bestDayAvg: number | null = null

    if (!isMultiDayResolution(res)) {
        const wdTotal    = new Array(7).fill(0)
        const wdDayCount = new Array(7).fill(0)
        const seenDays   = new Set<string>()

        // Use only the dominant series for weekday bucketing in multi-series
        // mode to avoid counting the same calendar day N times (once per zone).
        const sourceForWd = isMultiSeries ? [dominant.series] : rawResults

        sourceForWd.forEach(series => {
            series.data.forEach(d => {
                const m = moment(d.start)
                const wdIdx = isoWdToIdx(m.isoWeekday())
                // Deduplicate by calendar day so sub-day intervals don't inflate
                const dayKey = m.format('YYYY-MM-DD') + '-' + wdIdx
                wdTotal[wdIdx] += Number(d.count)
                if (!seenDays.has(dayKey)) {
                    seenDays.add(dayKey)
                    wdDayCount[wdIdx]++
                }
            })
        })

        const distinctWds = wdDayCount.filter(c => c > 0).length
        if (distinctWds >= 2) {
            for (let i = 0; i < 7; i++) {
                if (wdDayCount[i] === 0) continue
                const avg = wdTotal[i] / wdDayCount[i]
                if (bestDayAvg === null || avg > bestDayAvg) {
                    bestDayAvg = avg
                    bestDayIdx = i
                }
            }
        }
    }

    return {
        total,
        peak: peakCount > 0 ? { label: peakLabel, count: peakCount, resLabel } : null,
        bestDayIdx,
        bestDayAvg: bestDayAvg !== null ? Math.round(bestDayAvg) : null,
    }
}

// ─── KPI card — matches ReportYearly inline design ───────────────────────────
interface IKpiCardProps {
    label: string
    value: string
    subtext?: string
    accent?: boolean
}
const KpiCard = ({ label, value, subtext, accent }: IKpiCardProps) => (
    <div style={{
        flex: '1 1 0',
        minWidth: 160,
        background: accent ? 'linear-gradient(135deg, #A81A40, #C9375F)' : '#fff',
        color: accent ? '#fff' : '#333',
        borderRadius: 12,
        padding: '18px 16px',
        boxShadow: accent ? '0 4px 16px rgba(168,26,64,0.25)' : '0 1px 6px rgba(0,0,0,0.08)',
        border: accent ? 'none' : '1px solid #eee',
    }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.5px', opacity: 0.8, marginBottom: 6 }}>
            {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>
            {value}
        </div>
        {subtext && (
            <div style={{ fontSize: 11, marginTop: 5, opacity: 0.7 }}>
                {subtext}
            </div>
        )}
    </div>
)

// ─── Component ───────────────────────────────────────────────────────────────

const GraphAttendance = (props: IProps) => {
    const lingui = useLingui()

    const [loading, setLoading] = useState(false)
    const [rawResults, setRawResults] = useState<IGraphData[]|undefined>()
    const [graphResults, setGraphResults] = useState<IGraphData[]|undefined>()
    const [overlayMode, setOverlayMode] = useState(false)
    const [graphData, setGraphData] = useState<IGraphContentData>({
        series: [],
        categories: []
    })
    
    useEffect(() => {
        setLoading(true)
        let isMounted = true

        getAttendanceStats({
            ...props.filter,
            graphView: props.filter.type === EStatisticsType.table?undefined:props.filter.graphView
        })
            .then(res => {
                if(isMounted) {
                    setLoading(false)
                    setRawResults(res)
                    setGraphResults(res)
                }
            })
            .catch(e => {
                if(isMounted) {
                    setLoading(false)
                    setRawResults(undefined)
                    setGraphResults(undefined)
                    setGraphData({ series: [], categories: [] })
                }
            })
        return () => { isMounted = false }
    }, [props.filter])

    useEffect(() => {
        if (props.filter.type === EStatisticsType.xy && overlayMode && rawResults) {
            setGraphData(parseGraphDataOverlay(rawResults, lingui.i18n))
        } else {
            setGraphData(parseGraphDataAttendance(rawResults ? rawResults : [], props.filter, lingui.i18n))
        }
    }, [rawResults, overlayMode, props.filter, lingui.i18n])

    const kpi = useMemo(() => computeKpi(rawResults, props.filter), [rawResults, props.filter])
    const hasData = kpi.peak !== null || kpi.total !== null

    let graphTitle = t`Multi`
    let isTime = false;

    if(props.filter.graphView === EStatisticsGraphView.date) {
        graphTitle = t`Dátum`
    } else if (props.filter.graphView === EStatisticsGraphView.zone) {
        graphTitle = t`Zóna`
    }
    let yTitle =  t`Počet návštevníkov`
    let dataToSend: {
        date: {
            from: string,
            to: string
        }[],
        type: EStatisticsType,
        interval?: EStatisticsInterval,
        locations?: number[],
        zones?: number[],
        group?: EStatisticsGraphView
    } = {
        date: props.filter.dateRanges,
        type: props.filter.type,
        interval: props.filter.interval,
        locations: props.filter.localities,
        zones: props.filter.zones,
        group: props.filter.graphView
    }

    let objJsonStr = JSON.stringify(dataToSend);
    let objJsonB64 = Buffer.from(objJsonStr).toString("base64");

    // In overlay mode, force hour-of-day X axis
    const effectiveInterval = overlayMode ? EStatisticsInterval.min60 : props.filter.interval

    return (
        <>
            <Loader value={loading}/>

            {/* ── KPI cards ── */}
            {hasData && (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
                    {kpi.total !== null && (
                        <KpiCard
                            accent
                            label={t`Celkom návštevníkov`}
                            value={numberWithSpaces(kpi.total)}
                        />
                    )}
                    {kpi.peak && (
                        <KpiCard
                            label={t`Špičkový ` + kpi.peak.resLabel}
                            value={numberWithSpaces(kpi.peak.count)}
                            subtext={kpi.peak.label}
                        />
                    )}
                    {kpi.bestDayIdx !== null && (
                        <KpiCard
                            label={t`Najlepší deň`}
                            value={WEEKDAY_SK_FULL[kpi.bestDayIdx]}
                            subtext={`Ø ${numberWithSpaces(kpi.bestDayAvg!)} návšt./deň`}
                        />
                    )}
                </div>
            )}

            {props.filter.type === EStatisticsType.column && (
                <ColumnGraph graphTitle={graphTitle} graphData={graphData}/>
            )}
            {(props.filter.type === EStatisticsType.cake) && (
                <PieGraph graphTitle={graphTitle} graphData={graphData}/>
            )}
            {(props.filter.type === EStatisticsType.xy) && (
                <>
                    <div className="grid align-center pdb-10">
                        <div className="col_fill" />
                        <div className="col_default">
                            <div className="btn_switch" role="group" aria-label={t`Režim zobrazenia`}>
                                <button
                                    type="button"
                                    className={`btn_switch--item${!overlayMode ? ' active' : ''}`}
                                    onClick={() => setOverlayMode(false)}
                                    title={t`Sériový režim – dni za sebou`}
                                >
                                    <Trans>Séria</Trans>
                                </button>
                                <button
                                    type="button"
                                    className={`btn_switch--item${overlayMode ? ' active' : ''}`}
                                    onClick={() => setOverlayMode(true)}
                                    title={t`Prekryvný režim – dni na rovnakej osi`}
                                >
                                    <Trans>Prekryv</Trans>
                                </button>
                            </div>
                        </div>
                    </div>
                    <XYGraph graphTitle={graphTitle} graphData={graphData} interval={effectiveInterval} isTime={isTime} yTitle={yTitle} overlayMode={overlayMode} />
                </>
            )}
            {(props.filter.type === EStatisticsType.table) && (
                <TableGraph graphResults={graphResults} filter={objJsonB64} interval={props.filter.interval}/>
            )}
        </>
    )
};

export default GraphAttendance
